import { useState } from "react";
import { IssueRow } from "components/Molecules/IssueRow/IssueRow";
import { Colors, Icon } from "swing-components";

import { translate } from "~utils";

import { ReportIssueButtonProps } from "../AdminExperience/AdminExperience";
import { useReportIssueModal } from "../AdminExperience/hooks";
import { ReportIssueKey, ReportIssueKeyType } from "../AdminExperience/types";

/********* Types ********/
type IssueListBase = ReportIssueButtonProps & {
  onEditClickProp?: (intervalId: number) => void;
};

type IssueTruncatedList = IssueListBase & {
  variant: "TRUNCATED";
};

type IssueFullList = IssueListBase & {
  variant: "FULL";
};

type IssueListProps = IssueFullList | IssueTruncatedList;

const TRUNCATE_CUTOFF = 3;

export function IssueList(props: IssueListProps) {
  const { intervals, requestId, status, subId, subName, variant, school, onEditClickProp } = props;

  /********* Hooks ********/
  const [selectedInterval, setSelectedInterval] = useState<number>();
  const [selectedPanel, setSelectedPanel] = useState<ReportIssueKeyType>(
    ReportIssueKey.ReportIssueFormPanel,
  );

  const { presentModal } = useReportIssueModal({
    school,
    subId,
    subName,
    intervals,
    status,
    requestId,
    initialState: {
      initialSelectedIntervalId: selectedInterval,
      initialPanel: selectedPanel,
    },
  });

  /********* Handlers ********/
  function onEditClick(intervalId: number) {
    if (onEditClickProp) {
      onEditClickProp(intervalId);
      return;
    }

    setSelectedPanel(ReportIssueKey.ReportIssueFormPanel);
    setSelectedInterval(intervalId);
    presentModal();
  }

  /********* Constants ********/
  const intervalsWithIssues = intervals.filter((item) => {
    return item.issues.length > 0;
  });

  const numberOfIssuesToDisplay =
    variant === "TRUNCATED" ? TRUNCATE_CUTOFF : intervalsWithIssues.length;

  const hasIssues = intervalsWithIssues.length > 0;
  // The scenario here is on the SubInfoCard,
  // when no issues are found we do not want to show the component
  const showTruncated = variant === "TRUNCATED" ? hasIssues : true;

  const isTruncated = intervalsWithIssues.length > TRUNCATE_CUTOFF && variant === "TRUNCATED";

  /********* Render ********/
  return (
    <>
      {variant === "TRUNCATED" && hasIssues && (
        <div css={{ borderBottom: `1px solid ${Colors.slate200}`, marginTop: 16 }} />
      )}
      {showTruncated && (
        <div css={{ background: Colors.white200, paddingTop: 16 }}>
          <p css={{ margin: 0, marginBottom: 8, fontWeight: 600 }}>
            {translate("LABEL_ISSUES_LIST")}
          </p>
          {intervalsWithIssues.map((interval, index) => {
            return (
              interval.issues.length > 0 &&
              index < numberOfIssuesToDisplay && (
                <IssueRow
                  onEditClick={() => onEditClick(interval.eid)}
                  key={index}
                  issues={interval.issues}
                  date={interval.localStart}
                  // Truncated variant lives in <AdminExperience/>
                  // so remove the bottom border for the last issue row
                  // in order to allow <AdminExperience/> to control the bottom border
                  // in the overall layout.
                  isLast={variant === "TRUNCATED" && index === intervalsWithIssues.length - 1}
                />
              )
            );
          })}
          {isTruncated && (
            <div
              css={{
                float: "right",
                display: "flex",
                alignItems: "center",
                paddingTop: 10,
                paddingRight: 9,
                cursor: "pointer",

                "@media screen and (min-width: 768px)": {
                  paddingRight: 13,
                },
              }}
              onClick={() => {
                setSelectedPanel(ReportIssueKey.ReportIssueListPanel);
                presentModal();
              }}
            >
              <span css={{ color: Colors.blue500 }}>{translate("LABEL_VIEW_ALL_ISSUES")}</span>
              <Icon name="Chevron Right" color={Colors.blue500} />
            </div>
          )}
        </div>
      )}
    </>
  );
}
